import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { catchError, delay, map, mergeMap } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';
import { environment } from '../environments/environment';
import SHA256 from 'crypto-js/sha256';
import MD5 from 'crypto-js/md5';
import utf8 from 'utf8';
import SHA1 from 'crypto-js/sha1';
import { ActivatedRoute, Router } from '@angular/router';

import { AppService } from './app.service';
import { HomeService } from './home/home.service';

import { User } from './shared/interfaces/user';
import { Gender } from './shared/enum/Gender';
import { ErrorReturn } from './shared/interfaces/errorReturn';

export enum Attraction {
  woman = Gender.man,
  man = Gender.woman
}

export interface UserData {
  sex?: string, 
  birthdate?: string, 
  city?: { 
    name: string, 
    id: number 
  }, 
  name?: string, 
  email?: string, 
  password?: string
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  subscriptionFormData = new FormData()
  token!: string | undefined
  readonly country = environment.country
  public logged$ = new BehaviorSubject<boolean>(false)
  public authMe$ = new BehaviorSubject<User | null>(null)
  public user = new BehaviorSubject<User | null>(null)
  public fingerPrint: string = ''
  public userBilledInfos = null
  public userLogin = null
  private _localStorage: Storage | null = null
  savedImageUrl$ = new BehaviorSubject<string>('')
  public userBilled = false
  private _registrationNeeded = new BehaviorSubject<boolean>(false)
  public readonly registrationNeeded$ = this._registrationNeeded.asObservable().pipe(delay(1))
  genericUserData: UserData | null = null
  showChoiceList = new BehaviorSubject<boolean>(false)

  get authMe(): Observable<User | null> {
    return this.authMe$.asObservable()
  }

  get logged(): Observable<boolean> {
    this.logged$.next(!!this.get('token'))
    return this.logged$.asObservable()
  }
  
  setSubscriptionFormData(key: string, value: string){
    this.subscriptionFormData.set(key, value)

    // Display the values
    // for (const value of this.subscriptionFormData.values()) {
    //   console.log(value);
    // }
  }

  setRegistrationAsNeeded() {
    this._registrationNeeded.next(true)
  }

  setRegistrationAsUnneeded() {
    this._registrationNeeded.next(false)
  }

  constructor(
    private http: HttpClient, 
    private appService: AppService, 
    private router: Router,
    private homeService: HomeService,
    private route: ActivatedRoute,
    @Inject(DOCUMENT) private document: Document
  ) {
    this._localStorage = this.document.defaultView?.localStorage || null;
  }

  get(key: string): string {
    //console.log(this.localStorage?.getItem(key))
    return this._localStorage?.getItem(key) || '';
  }

  set(key: string, value: string): void {
    this._localStorage?.setItem(key, value);
  }

  remove(key: string): void {
    return localStorage.removeItem(key);
  }

  setAuthMe(value: User) {
    this.authMe$.next(value);
    this.user.next(value)
  }

  authLogin(login: string, password: string) : Observable<User> {
    return this.http
      .get<{body :{ user: User; api_token: string }}>(
        `${this.appService.API}/connect/login/${login}/${this.dveHash(password)}/${this.appService.serviceId}`
      )
      .pipe(
        map(
          (res) => {
            console.log(res.body.user)
            
            this.set('user', JSON.stringify(res.body.user))
            this.set('user_id', (res.body.user.id).toString())
            this.set('token', res.body.api_token);

            this.setAuthMe(res.body.user)
            this.user.next(res.body.user)

            return res.body.user;
          }),
          catchError((error) => {
            console.error(error)
            return throwError(() => error);
          }),
        );
  }

  deleteAccount(password: string): Observable<{error: boolean, message: string}> {
    return this.http
      .delete<{ error_code: number }>(`${this.appService.API}/api/user?password=${this.dveHash(password)}`)
      .pipe(
        map(() => {
          return {error: false, message: 'Account deleted'};
        }),
        catchError((res) => {
          if(res.error.error_code === 20){
            return of({error: true, message: $localize `:@@ERROR_WRONG_PASSWORD:ERROR. Your password seems to be wrong. Please check your information.`})
          }
          return of({error: true, message: 'We could not delete your password.'})// TODO : à traduire ?
        }),
      );
  }

  authDisconnect() {
    this._localStorage?.clear()
    this.authMe$.next(null)
    this.user.next(null)
    this.router.navigate(['/login'])
    this.homeService.stop()
  }

  setFingerPrint(): string {
    return (
      Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
    );
  }

  authSubscribe(): Observable<{datas: {user: {user_id: number, userdve_id: string},api_token: string | undefined}}> {
    const passhash = this.dveHash(this.subscriptionFormData.get('password') as string)
    const body = {
      attraction: this.subscriptionFormData.get('sex') === 'man' ? Gender.woman : Gender.man,
      birthdate: this.subscriptionFormData.get('birthdate') || this.get('birthdate'),
      city_id: parseInt(this.subscriptionFormData.get('city_id') as string) || parseInt(this.get('city_id')),
      firstname: this.subscriptionFormData.get('firstname') as string || this.get('firstname'),
      email: this.subscriptionFormData.get('email') as string || this.get('email'),
      password: passhash,
      sex: this.subscriptionFormData.get('sex') || this.get('sex'),
      product_id: this.appService.serviceId
    }

    return this.http.post<{datas: {user: {user_id: number, userdve_id: string},api_token: string | undefined}}>(`${this.appService.API}/registration/register`, body)
    .pipe(
      map((res) => {
        return res
      })
    )
  }

  // the user is registered on kliento but not on the database
  authSubscribePostBilling(value: {
    attraction: string;
    birthdate: string;
    city_id: number;
    firstname: string;
    email: string;
    password: string;
    sex: string;
    phoneNumber: string;
  }): Observable<{ user: { user_id: number; userdve_id: number }; api_token: string }> {
    const passhash = this.dveHash(value.password)
    const body = {
      phoneNumber: value.phoneNumber,
      attraction: value.attraction,
      birthdate: value.birthdate,
      city_id: value.city_id,
      firstname: value.firstname,
      email: value.email,
      password: passhash,
      sex: value.sex,
      service_id: this.appService.serviceId,
      product_id: this.appService.serviceId.toString(),
    }

    return this.http
      .post<{ user: { user_id: number; userdve_id: number }; api_token: string }>(`${this.appService}/api/registration/register/withbilling`, body)
      .pipe(
        map((res) => {
          this.remove('subtoken')
          return res
        })
      )
  }

  setToken(subscription: string): string {
    const hash = SHA256(utf8.encode(this.fingerPrint));
    return btoa(utf8.encode(subscription + '__' + hash));
  }

  authUpdate(): Observable<User>{
    let description = ''
    if(this.subscriptionFormData.get('description') === 'null') description = ''
    else {
      console.log('autre cas')
      description = this.subscriptionFormData.get('description') as string
    }

    const body = {
      description: description,	// || this.authMe$.getValue()?.description || ''
      firstname: this.subscriptionFormData.get('firstname') || this.authMe$.getValue()?.firstname,
      birthdate: this.subscriptionFormData.get('birthdate') || this.authMe$.getValue()?.birthdate,
      Sexual: this.authMe$.getValue()?.attraction === 'man' ? Attraction.woman : Attraction.man,
      city_id: parseInt(this.subscriptionFormData.get('city_id') as string) || this.authMe$.getValue()?.city?.id,
      email: this.subscriptionFormData.get('email') || this.authMe$.getValue()?.email
    }

    return this.http.put(`${this.appService.API}/api/user`, body).pipe(
      mergeMap(() => {
        return this.getProfile(Number(this.get('user_id'))).pipe(
          map((user) => {
            this.setAuthMe(user)
            return user
          }),
          catchError((error) => {
            return throwError(() => error)
          }),
        );
      })
    );
  }

  authUpdatePassword(value: { old: string; new: string }): Observable<{code: number} | object> {
    return this.http
      .put(
        `${this.appService.API}/api/user/password?old_password=${this.dveHash(
          value.old
        )}&new_password=${this.dveHash(value.new)}`,
        {}
      )
      .pipe(
        map((res) => {
          return res;
        }),
        catchError(() => {
          const err: ErrorReturn = {
            code: 500,
            error_code: 404,
            level: '',
            message: 'Wrong current password',
          }

          this.appService.setPopin(err);
          
          return throwError(() => err)
        })
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  authRecoverPassword(email: string): Observable<any> {
    // TODO: Route ne fonctionne pas. Problème de CORS. Aucune réponse. Voir avec Conor.
    return this.http.get(`${this.appService.API}/connect/api/user/sendResetPasswordMail/${email}/${this.appService.serviceId}`).pipe(
      map(res => {
        console.log(res)
        return res
      }),
      catchError(err => {
        return throwError(() => err)
      })
    )
  }

  getAuthContacts(page: number): Observable<{
    users: User[];
    counters: { general: number; invitations: number };
  }> {
    return this.http
      .get<{ users: User[]; counters: { general: number; invitations: number } }>(
        `${this.appService.API}/relationship/my/${Date.now()}/${page}/10`
      )
      .pipe(
        map((res) => {
          return {
            users: res.users,
            counters: res.counters,
          };
        })
      );
  }

  getAuthContactsRequest(page: number): Observable<User[]> {
    return this.http
      .get<{ users: User[] }>(`${this.appService.API}/api/relationship/invitations/FR/${page}/10`)
      .pipe(
        map((res) => {
          return res.users;
        }),
        catchError(error => {
          console.log(error)
          return throwError(() => error)
        })
      );
  }

  getProfile(id: number): Observable<User> {
    const str = id.toString()
    
    return this.http
      .get<{ user: User; talking_id?: number; connected_user: {isSubscribed: boolean;}; already_seen: boolean }>(`${this.appService.API}/api/user/get/${str}/fr`)
      .pipe(
        map((res) => {
          res.user.talking_id = res.talking_id || undefined
          res.user.isSubscribed = res.connected_user.isSubscribed
          res.user.already_seen = res.already_seen
          return res.user
        }),
        catchError((error) => {
          console.log(error)
          return throwError(() => error);
        }),
      );
  }

  dveHash(pass: string): string {
    const salt = MD5(SHA1('AN65Bef2sW6n').toString().substring(3, 27)).toString()
    
    const ret = SHA1(salt.substring(0, 10) + pass + salt.substring(10, 32)).toString()
    return ret;
  }

  checkTokenValidity(): Observable<boolean>{
    return this.http.get<{is_valid_token: boolean}>(`${this.appService.API}/connect/token-validity`)
    .pipe(
      map(
        (res) => {
          return res.is_valid_token
        }),
        catchError((error) => {
          return throwError(() => error);
        }),
      );
  }

  isAuthenticated(): boolean {
    const token = this.get('token')

    if(token){
      this.checkTokenValidity().subscribe(res => {
        if(this.route.children[0].snapshot.url[0].path.includes('subscribe')){
          if(!res){
            // invalid token
            // not logged in: we stay on the subscribe page
            return true
          } else {
            // valid token
            let user!: User | null 
            const id = this.get('user_id')
    
            this.getProfile(parseInt(id)).subscribe(res => {
              user = res
              if(!this.user.value) this.user.next(user)
    
              if(user && !user.main_photo){
                // logged in without any main photo: /upload-photo
                this.router.navigate(['/upload-photo'])
              } else {
                // logged in with main photo: /home
                this.homeService.pageDisplayed.next('home')
                this.router.navigate(['/home'])
              }
            })
    
            return true
          }
        } else {
          if(!res){
            // invalid token
            // not logged in: /login
            this._localStorage?.clear()
            this.router.navigate(['/login'])
            return false
          } else {
            // valid token
            let user!: User | null 
            const id = this.get('user_id')
    
            this.getProfile(parseInt(id)).subscribe(res => {
              user = res
              if(!this.user.value) this.user.next(user)
    
              if(user && !user.main_photo){
                // logged in without any main photo: /upload-photo
                this.router.navigate(['/upload-photo'])
              }
            })
    
            return true
          }
        }
      })
    } else {
      // I do not have any token
      if(this.route.children[0] && this.route.children[0].snapshot.url[0].path.includes('subscribe')){
        return true
      } else {
        this._localStorage?.clear()
        this.router.navigate(['/login'])
        return false
      }
    }

    return true
  }

  // know whether the user is registered on kliento and on the database
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  authCheckOperationId(data: { operation: string | null; token: string | null }): Observable<any> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.http.get<any>(
      `${this.appService.API}/billing/userexists?${
        data.operation ? 'operation_id=' + data.operation : ''
      }${data.token ? '&authtoken=' + data.token : ''}&product_id=113`
    ).pipe(
      map(
        (res) => {
          return res
        }
      ),
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }

  // get auto-generated user data from the api to pre-fill subscription form
  getGenericUserData(): Observable<UserData>{
    return this.http.get<{data: UserData}>(`${this.appService.API}/registration/autocomplete/${this.appService.serviceId}`)
    .pipe(
      map(res => {
        this.genericUserData = res.data
        return res.data
      }),
      catchError(err => {
        return throwError(() => err)
      })
    )
  }

  // A supprimer : uniquement pour les tests !!! 
  // validateQuizz(){
  //   const body = {
  //     quizz_id: 28,
  //     answers: [321,323,325,327,329,331]
  //   }

  //   return this.http.post(`${this.appService.API}/api/quizz/answers`, body)
  //   .pipe(
  //     map(res=> {
  //       console.log(res)
  //     })
  //   )
  // }
  // A supprimer : uniquement pour les tests !!!   
}